<template>
  <div class="heumieum-upload-request-page">
    <LoadingOverlay :show="loading" text="불러오는 중..." variant="light" />

    <header class="console-page-head">
      <div class="console-page-copy">
        <p class="console-kicker">슈퍼바이저</p>
        <h2 class="console-title">희망이음 업로드</h2>
        <p class="console-desc">기관이 올린 등록요청과 완료 내역입니다.</p>
      </div>
      <div class="console-head-side">
        <button type="button" class="hur-refresh" :disabled="loading" @click="loadItems">
          새로고침
        </button>
      </div>
    </header>

    <div class="hur-summary">
      <button type="button" class="hur-stat" :class="{ active: statusFilter === 'all' }" @click="statusFilter = 'all'">
        <span class="hur-stat-value">{{ items.length }}</span>
        <span class="hur-stat-label">전체</span>
      </button>
      <button type="button" class="hur-stat is-requested" :class="{ active: statusFilter === 'requested' }" @click="statusFilter = 'requested'">
        <span class="hur-stat-value">{{ requestedCount }}</span>
        <span class="hur-stat-label">등록요청</span>
      </button>
      <button type="button" class="hur-stat is-completed" :class="{ active: statusFilter === 'completed' }" @click="statusFilter = 'completed'">
        <span class="hur-stat-value">{{ completedCount }}</span>
        <span class="hur-stat-label">완료</span>
      </button>
      <label class="hur-search">
        <input v-model="keyword" type="text" placeholder="기관, 통장, 기관기호" />
      </label>
    </div>

    <div class="console-panel">
      <table class="hur-table">
        <thead>
          <tr>
            <th>기관</th>
            <th>사업구분</th>
            <th>연월</th>
            <th>통장</th>
            <th>상태</th>
            <th>요청일시</th>
            <th>완료일시</th>
            <th>메모</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!loading && filteredItems.length === 0">
            <td colspan="8" class="hur-empty">표시할 요청이 없습니다.</td>
          </tr>
          <tr v-for="(item, index) in filteredItems" :key="rowKey(item, index)">
            <td>
              <div class="hur-facility">
                <span class="hur-facility-name">{{ item.memberName || '-' }}</span>
                <span v-if="item.memberCode" class="hur-facility-code">{{ item.memberCode }}</span>
              </div>
            </td>
            <td>{{ item.bizGubun || '-' }}</td>
            <td>{{ formatPeriod(item) }}</td>
            <td>
              <div class="hur-bank">
                <span>{{ item.bankAlias || '-' }}</span>
                <span v-if="item.bankName" class="hur-bank-name">{{ item.bankName }}</span>
              </div>
            </td>
            <td>
              <span class="hur-badge" :class="item.status === 'completed' ? 'is-completed' : 'is-requested'">
                {{ statusLabel(item.status) }}
              </span>
            </td>
            <td>{{ formatDateTime(item.requestedAt) }}</td>
            <td>{{ item.status === 'completed' ? formatDateTime(item.completedAt) : '-' }}</td>
            <td class="hur-memo">{{ item.memo || '-' }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import { closingApi } from '@/services/api';

interface RequestItem {
  memberName: string;
  memberCode: string;
  bizGubun: string;
  year: number;
  month: number;
  bankAlias: string;
  bankName: string;
  status: 'requested' | 'completed' | string;
  requestType: string;
  requestedAt: string | null;
  completedAt: string | null;
  memo: string;
}

const loading = ref(false);
const items = ref<RequestItem[]>([]);
const requestedCount = ref(0);
const completedCount = ref(0);
const statusFilter = ref<'all' | 'requested' | 'completed'>('requested');
const keyword = ref('');

const filteredItems = computed(() => {
  const word = keyword.value.trim().toLowerCase();
  return items.value.filter((item) => {
    if (statusFilter.value !== 'all' && item.status !== statusFilter.value) return false;
    if (!word) return true;
    const haystack = [item.memberName, item.memberCode, item.bankAlias, item.bankName, item.bizGubun]
      .join(' ')
      .toLowerCase();
    return haystack.includes(word);
  });
});

const statusLabel = (status: string) => (status === 'completed' ? '완료' : '등록요청');

const formatPeriod = (item: RequestItem) => {
  const month = String(item.month || '').padStart(2, '0');
  return `${item.year}.${month}`;
};

const formatDateTime = (value: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}.${m}.${d} ${h}:${min}`;
};

const rowKey = (item: RequestItem, index: number) => {
  return `${item.memberCode}_${item.year}_${item.month}_${item.bankAlias}_${index}`;
};

const loadItems = async () => {
  loading.value = true;
  try {
    const response = await closingApi.heumieumRequestBoard();
    const data = response.data?.data || {};
    items.value = data.items || [];
    requestedCount.value = Number(data.requested || 0);
    completedCount.value = Number(data.completed || 0);
  } catch (error: any) {
    items.value = [];
    requestedCount.value = 0;
    completedCount.value = 0;
    const message = error.response?.data?.message || error.message || '조회에 실패했습니다.';
    window.alert(message);
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  loadItems();
});
</script>
